import { Box, Button, Dialog, DialogActions, DialogContent, Divider, Grid, TextField, Typography } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import React from 'react';
import StyledCardTitleBar from '../../../../../common/StyledCardTitleBar';

interface DateRangeDialogProps {
    open: boolean;
    onClose: () => void;
    tempStartDate: moment.Moment | null;
    tempEndDate: moment.Moment | null;
    setTempStartDate: (date: moment.Moment | null) => void;
    setTempEndDate: (date: moment.Moment | null) => void;
    onApply: () => void;
    onSelectPredefinedRange: (range: string) => void;
}

const DateRangeDialog: React.FC<DateRangeDialogProps> = ({
    open,
    onClose,
    tempStartDate,
    tempEndDate,
    setTempStartDate,
    setTempEndDate,
    onApply,
    onSelectPredefinedRange,
}) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <StyledCardTitleBar title="Select Date Range" />
            <DialogContent>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Quick Select
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={4}>
                                <Button variant="outlined" size="small" fullWidth onClick={() => onSelectPredefinedRange('thisMonth')}>
                                    This Month
                                </Button>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <Button variant="outlined" size="small" fullWidth onClick={() => onSelectPredefinedRange('lastMonth')}>
                                    Last Month
                                </Button>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <Button variant="outlined" size="small" fullWidth onClick={() => onSelectPredefinedRange('last3Months')}>
                                    Last 3 Months
                                </Button>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <Button variant="outlined" size="small" fullWidth onClick={() => onSelectPredefinedRange('thisQuarter')}>
                                    This Quarter
                                </Button>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <Button variant="outlined" size="small" fullWidth onClick={() => onSelectPredefinedRange('lastQuarter')}>
                                    Last Quarter
                                </Button>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <Button variant="outlined" size="small" fullWidth onClick={() => onSelectPredefinedRange('thisYear')}>
                                    This Year
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Typography variant="subtitle2">Custom Range</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <DatePicker
                                label="Start Date"
                                value={tempStartDate}
                                minDate={moment().subtract(1, 'year')}
                                onChange={(newValue: moment.Moment | null) => {
                                    if (!newValue) {
                                        return;
                                    }

                                    setTempStartDate(newValue.startOf('day'));
                                }}
                                renderInput={params => <TextField {...params} fullWidth margin="normal" helperText={null} />}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <DatePicker
                                label="End Date"
                                value={tempEndDate}
                                minDate={tempStartDate || undefined}
                                onChange={(newValue: moment.Moment | null) => {
                                    if (!newValue) {
                                        return;
                                    }

                                    setTempEndDate(newValue.endOf('day'));
                                }}
                                renderInput={params => <TextField {...params} fullWidth margin="normal" helperText={null} />}
                            />
                        </Grid>
                    </Grid>
                </LocalizationProvider>
            </DialogContent>
            <DialogActions
                sx={{
                    backgroundColor: theme => theme.palette.grey[100],
                    borderTop: theme => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={onApply} variant="contained" color="primary" disabled={!tempStartDate || !tempEndDate}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DateRangeDialog;
